/* sa-animations.js
   Namespaced, low-cost JS for subtle scroll reveals, parallax, and tilt.
   Uses IntersectionObserver + rAF for performance.
*/
(function () {
  'use strict';
  // quick escape if user prefers reduced motion
  const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReduced) return;

  // Helper: throttle via rAF
  function rafThrottle(fn) {
    let running = false;
    return function (...args) {
      if (running) return;
      running = true;
      requestAnimationFrame(() => { fn.apply(this, args); running = false; });
    };
  }

  /* Repeatable fade-on-scroll observer (replace previous fade code) */
  (function () {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const fadeOptions = { threshold: 0.12 };
    const fadeObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const el = entry.target;
        if (entry.isIntersecting) {
          // Entering viewport -> add class to animate in
          el.classList.add('sa-in');
        } else {
          // Leaving viewport -> remove class so it can animate again on next enter
          el.classList.remove('sa-in');
        }
      });
    }, fadeOptions);

    // Observe any fade elements on DOMContentLoaded and on-demand via init
    function initFade(root = document) {
      root.querySelectorAll('.sa-fade, .sa-fade-left, .sa-fade-right, .sa-fade-text').forEach(el => {
        fadeObserver.observe(el);
      });
    }

    // auto-init for initial page load
    document.addEventListener('DOMContentLoaded', () => initFade(document));

    // expose init so dynamic sections can call saAnimations.init()
    window.saAnimations = window.saAnimations || {};
    // preserve any earlier helpers (tilt/parallax, etc.)
    const prevInit = window.saAnimations.init || function () {};
    window.saAnimations.init = function (root = document) {
      try { initFade(root); } catch (e) {}
      prevInit(root);
    };
  })();

    /* --- Gentle parallax for elements with [data-sa-parallax] --- */
    const parallaxNodes = Array.from(document.querySelectorAll('[data-sa-parallax]'));
    if (parallaxNodes.length) {
      const handleParallax = rafThrottle(() => {
        const viewportHeight = window.innerHeight;
        parallaxNodes.forEach(node => {
          // small parallax: map element center position to translateY
          const rect = node.getBoundingClientRect();
          const center = rect.top + rect.height / 2;
          // percent from -1 to 1 across viewport
          const pct = (center - viewportHeight / 2) / (viewportHeight / 2);
          const speed = parseFloat(node.dataset.saParallax) || parseFloat(node.getAttribute('data-sa-parallax')) || 0.14;
          // max translate in px (small)
          const maxTranslate = Math.min(60, Math.max(20, Math.abs(speed) * 200));
          const translateY = -pct * maxTranslate * speed;
          node.style.transform = `translate3d(0, ${translateY}px, 0)`;
        });
      });
      window.addEventListener('scroll', handleParallax, { passive: true });
      window.addEventListener('resize', handleParallax);
      // initial call
      window.requestAnimationFrame(() => handleParallax());
    }

  /* --- Mouse tilt for desktop cards (.sa-tilt) --- */
  const tiltNodes = Array.from(document.querySelectorAll('.sa-tilt'));
  const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  if (!isTouch && tiltNodes.length) {
    tiltNodes.forEach(node => {
      const maxDeg = Number(node.dataset.saTiltMax) || 7;
      const onMove = (e) => {
        const rect = node.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const cx = rect.width / 2;
        const cy = rect.height / 2;
        const dx = (x - cx) / cx; // -1 to 1
        const dy = (y - cy) / cy;
        const rotY = dx * maxDeg;
        const rotX = -dy * maxDeg;
        node.style.transform = `rotateY(${rotY}deg) rotateX(${rotX}deg)`;
      };
      const onLeave = () => { node.style.transform = ''; };
      node.addEventListener('mousemove', rafThrottle(onMove));
      node.addEventListener('mouseleave', onLeave);
      node.addEventListener('blur', onLeave);
    });
  }

  /* Safety: allow manual init if dynamic sections are added */
  window.saAnimations = {
    init: function (root = document) {
      root.querySelectorAll('.sa-fade').forEach(el => {
        if (!el.classList.contains('sa-inited')) fadeObserver.observe(el);
      });
    }
  };
})();
